import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { PageFrame, PageHeader, Note, Button, TabBar } from '@/components/ui';

export interface OrderTab {
    key: string;
    label: string;
}

export interface OrderActionButton {
    label: string;
    onClick: () => void;
    variant: 'buy' | 'sell';
}

interface OrdersPageLayoutProps {
    title: string;
    /** One line under the title: what this page is for. */
    subtitle?: React.ReactNode;
    tabs: OrderTab[];
    activeTab: string;
    onTabChange: (key: string) => void;
    actionButtons: OrderActionButton[];
    successMessage?: string | null;
    errorMessage?: string | null;
    children: React.ReactNode;
}

/**
 * The shell both the equity and the ETF orders pages are built from: page
 * header with the two create actions, a tab strip, and the active tab's content.
 *
 * Buy is the page's primary action; sell is a secondary one beside it. The
 * green/red pair the header used to carry is reserved for gain and loss, and a
 * sell order is neither.
 */
const OrdersPageLayout: React.FC<OrdersPageLayoutProps> = ({
    title,
    subtitle,
    tabs,
    activeTab,
    onTabChange,
    actionButtons,
    successMessage,
    errorMessage,
    children,
}) => (
    <PageFrame
        header={
            <PageHeader
                title={title}
                subtitle={subtitle}
                actions={actionButtons.map((btn) => (
                    <Button
                        key={btn.label}
                        variant={btn.variant === 'buy' ? 'success' : 'danger'}
                        onClick={btn.onClick}
                        icon={btn.variant === 'buy' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    >
                        {btn.label}
                    </Button>
                ))}
            />
        }
    >
        {successMessage && <Note tone="success">{successMessage}</Note>}
        {errorMessage && <Note tone="error">{errorMessage}</Note>}

        <TabBar
            ariaLabel={`${title} stage`}
            value={activeTab}
            onChange={onTabChange}
            tabs={tabs.map((tab) => ({ value: tab.key, label: tab.label }))}
        />

        {children}
    </PageFrame>
);

export default OrdersPageLayout;
